import { useRef, useState, useEffect } from 'react';
import { Box, SxProps } from '@mui/material';

const padTime = (value: number): string => String(value).padStart(2, '0');

const getTimeUnits = (totalSeconds: number) => ({
  minutes: Math.floor(totalSeconds / 60),
  seconds: totalSeconds % 60,
});

interface ICountdownTimer {
  onCountdownFinish: () => void;
  sx?: SxProps;
  seconds?: number; // starting duration in seconds
}

/**
 * Resilient countdown that does NOT reset on parent re-renders.
 * It restarts only when the `seconds` prop actually changes.
 * Uses a fixed target timestamp and derives remaining time from `Date.now()` to avoid drift.
 */
const CountdownTimer = ({
  onCountdownFinish,
  sx,
  seconds = 0,
}: ICountdownTimer) => {
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const targetMsRef = useRef<number>(0); // absolute target in ms
  const finishedRef = useRef<boolean>(false);
  const prevSecondsRef = useRef<number | null>(null);
  const onFinishRef = useRef(onCountdownFinish);

  // keep latest finish handler without forcing restarts
  useEffect(() => {
    onFinishRef.current = onCountdownFinish;
  }, [onCountdownFinish]);

  const format = (remaining: number) => {
    const { minutes, seconds } = getTimeUnits(Math.max(remaining, 0));
    return `${padTime(minutes)}:${padTime(seconds)}`;
  };

  const [display, setDisplay] = useState<string>(format(seconds));

  function clearTimer() {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }

  function computeRemaining(): number {
    const now = Date.now();
    const diffMs = Math.max(0, targetMsRef.current - now);
    // ceil to hold the current integer until boundary passes (prevents bounce)
    return Math.ceil(diffMs / 1000);
  }

  function handleFinish() {
    if (finishedRef.current) {
      return;
    }

    finishedRef.current = true;
    clearTimer();
    setDisplay(format(0));

    try {
      onFinishRef.current?.();
    } catch (error) {
      // swallow user handler errors to keep timer stable
    }
  }

  function tick() {
    const remaining = computeRemaining();

    setDisplay((prev) => {
      const next = format(remaining);
      return next === prev ? prev : next;
    });
    if (remaining <= 0) {
      handleFinish();
    }
  }

  // Start/restart ONLY when `seconds` actually changes
  useEffect(() => {
    if (prevSecondsRef.current === seconds && intervalRef.current) {
      // seconds unchanged -> keep running, do nothing
      return;
    }

    // restart
    clearTimer();
    finishedRef.current = false;
    targetMsRef.current = Date.now() + Math.max(0, seconds) * 1000;
    setDisplay(format(seconds));

    if (seconds <= 0) {
      handleFinish();
    } else {
      intervalRef.current = setInterval(tick, 200);
    }

    prevSecondsRef.current = seconds;

    return () => {
      clearTimer();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seconds]);

  return <Box sx={sx}>{display}</Box>;
};

export default CountdownTimer;
